// src/Component/Workshop/Dashboard/Dashboard.jsx

import { useNavigate } from "react-router-dom";
import { useDashboardStats } from "../../../hooks/useWorkshopQueries";
import {
  Badge, Spinner, ErrorState,
  STAGE_LABELS, STATUS_LABELS, stageBadge, statusBadge, timeAgo,
} from "../UI";

// ── Mock data ─────────────────────────────────────────────────────────────────
const MOCK = {
  kpi: [
    { label:"Active Cases",     value:184, trend:"▲ 12% week", color:"#3b82f6", bg:"#1e3a5f" },
    { label:"In Survey",        value:56,  trend:"▲ +4 today", color:"#f97316", bg:"#3d2010" },
    { label:"In Workshop",      value:73,  trend:"▼ –2 today", color:"#a855f7", bg:"#2d1a45" },
    { label:"Pending Delivery", value:26,  trend:"",           color:"#22c55e", bg:"#0f3021" },
    { label:"Claims Settled",   value:312, trend:"",           color:"#14b8a6", bg:"#0c2d2b" },
    { label:"SLA Breaches",     value:8,   trend:"",           color:"#ef4444", bg:"#3b1414" },
  ],
  pipeline: [
    { stage:"CASE_CREATED",     no:"CPA1023", name:"Amit Sharma",  vehicle:"DL01AB1234", status:"DOCUMENTS_PENDING",   sla:"ON_TIME"  },
    { stage:"SURVEY_PROCESS",   no:"CPA1028", name:"Rajesh Verma", vehicle:"KA05CD5678", status:"SPOT_SURVEY_ASSIGNED", sla:"BREACHED" },
    { stage:"WORKSHOP_REPAIR",  no:"CPA1041", name:"Priya Singh",  vehicle:"MH12KYY678", status:"WORK_STARTED",         sla:"ON_TIME"  },
    { stage:"DELIVERY_PAYMENT", no:"CPA1055", name:"Sandeep Rao",  vehicle:"DL01AB1234", status:"PAYMENT_REQUEST_SENT", sla:"BREACHED" },
  ],
  recent: [
    { no:"CPA1023", vehicle:"DL01AB1234",  name:"Amit Sharma",  stage:"WORKSHOP_REPAIR",  status:"PART_ORDERED",         surveyor:"",          ago:"2 hours ago",  sla:"BREACHED" },
    { no:"CPA1028", vehicle:"KA05CD5678",  name:"Rajesh Verma", stage:"SURVEY_PROCESS",   status:"SPOT_SURVEY_ASSIGNED",  surveyor:"A. Mishra", ago:"2 hours ago",  sla:"ON_TIME"  },
    { no:"CPA1028", vehicle:"DL01AB1234",  name:"Rajesh Verma", stage:"SURVEY_PROCESS",   status:"SPOT_SURVEY_ASSIGNED",  surveyor:"",          ago:"1 day ago",    sla:"ON_TIME"  },
    { no:"CPA1028", vehicle:"HA226Y20001", name:"Rajesh Mehta",  stage:"WORKSHOP_REPAIR",  status:"WORKSHOP_ASSIGNED",     surveyor:"",          ago:"1 day ago",    sla:"BREACHED" },
    { no:"CPA1035", vehicle:"DL01AB1234",  name:"Rajesh Mehta",  stage:"DELIVERY_PAYMENT", status:"PAYMENT_REQUEST_SENT",  surveyor:"",          ago:"3 hours ago",  sla:"BREACHED" },
    { no:"CPA1070", vehicle:"RR26YZ9901",  name:"Rakesh Mehta",  stage:"CASE_CREATED",     status:"CASE_REGISTERED",       surveyor:"",          ago:"30 mins ago",  sla:"ON_TIME"  },
  ],
  slaAlerts: [
    { type:"Survey Delay",          caseNo:"CPA1028", detail:"Overdue 5 hrs",  color:"#f97316" },
    { type:"Workshop Intake Delay", caseNo:"CPA1015", detail:"Overdue 10 hrs", color:"#ef4444" },
    { type:"Parts Delay",           caseNo:"CPA1041", detail:"Overdue 2 days", color:"#ef4444" },
  ],
  timeline: [
    { time:"12:30 PM", dot:"#3b82f6", items:[
      { label:"Spot Survey", mid:" Completed by ", actor:"A. Mishra", actorColor:"#3b82f6" },
    ]},
    { time:"11:00 AM", dot:"#64748b", items:[
      { label:"Workshop Assigned", mid:" to ", actor:"Delhi Repair Center", actorColor:"#94a3b8" },
    ]},
    { time:"09:15 AM", dot:"#64748b", items:[
      { label:"Case Created", mid:" by ", actor:"Admin", actorColor:"#a855f7" },
    ]},
  ],
};

// Keep KPI visual identity in frontend and merge API values into this shape.
const KPI_META = [
  { key: "activeCases",     label: "Active Cases", trend:"▲ 12% week",    color: "#3b82f6", bg: "#1e3a5f" },
  { key: "inSurvey",        label: "In Survey",    trend:"▲ 12% week",    color: "#f97316", bg: "#3d2010" },
  { key: "inWorkshop",      label: "In Workshop",  trend:"▲ 12% week",    color: "#a855f7", bg: "#2d1a45" },
  { key: "pendingDelivery", label: "Pending Delivery",trend:"▲ 12% week", color: "#22c55e", bg: "#0f3021" },
  { key: "claimsSettled",   label: "Claims Settled",  trend:"▲ 12% week", color: "#14b8a6", bg: "#0c2d2b" },
  { key: "slaBreaches",     label: "SLA Breaches",  trend:"▲ 12% week",   color: "#ef4444", bg: "#3b1414" },
];

// ── KPI icon SVGs ─────────────────────────────────────────────────────────────
const KPI_ICONS = {
  0: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.6"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>,
  1: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="1.6"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>,
  2: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#a855f7" strokeWidth="1.6"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>,
  3: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.6"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
  4: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#14b8a6" strokeWidth="1.6"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>,
  5: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.6"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
};

// Stage label short versions for pipeline header
const STAGE_SHORT = {
  CASE_CREATED:      "Case Created",
  SURVEY_PROCESS:    "Survey Process",
  WORKSHOP_REPAIR:   "Workshop Repair",
  DELIVERY_PAYMENT:  "Delivery & Payment",
  CLAIM_SETTLEMENT:  "Claim Settlement",
};

// Status → display label (short)
const STATUS_SHORT = {
  DOCUMENTS_PENDING:    "Docs Pending",
  SPOT_SURVEY_ASSIGNED: "Spot Survey Assigned",
  WORK_STARTED:         "Work Started",
  PAYMENT_REQUEST_SENT: "Payment R…",
  PART_ORDERED:         "Parts Ordered",
  WORKSHOP_ASSIGNED:    "Workshop Assigned",
  CASE_REGISTERED:      "Registered",
};

// Status → badge color
const STATUS_COLOR = {
  DOCUMENTS_PENDING:    { bg:"rgba(234,179,8,.18)",  text:"#eab308", border:"rgba(234,179,8,.3)"  },
  SPOT_SURVEY_ASSIGNED: { bg:"rgba(168,85,247,.18)", text:"#a855f7", border:"rgba(168,85,247,.3)" },
  WORK_STARTED:         { bg:"rgba(249,115,22,.18)", text:"#f97316", border:"rgba(249,115,22,.3)" },
  PAYMENT_REQUEST_SENT: { bg:"rgba(20,184,166,.18)", text:"#14b8a6", border:"rgba(20,184,166,.3)" },
  PART_ORDERED:         { bg:"rgba(249,115,22,.18)", text:"#f97316", border:"rgba(249,115,22,.3)" },
  WORKSHOP_ASSIGNED:    { bg:"rgba(34,197,94,.18)",  text:"#22c55e", border:"rgba(34,197,94,.3)"  },
  CASE_REGISTERED:      { bg:"rgba(59,130,246,.18)", text:"#3b82f6", border:"rgba(59,130,246,.3)" },
};

function StatusPill({ status }) {
  const c = STATUS_COLOR[status] || { bg:"rgba(100,116,139,.18)", text:"#94a3b8", border:"rgba(100,116,139,.3)" };
   const label = STATUS_SHORT[status] || status.replace(/_/g," ")
  return (
    <span title={label} style={{
      fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:20,
      background:c.bg, color:c.text, border:`1px solid ${c.border}`,
      display:"inline-block", maxWidth:"100%",
      overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
    }}>
      {label}
    </span>
  );
}

// Stage badge for recent cases table (plain text style like reference)
const STAGE_TABLE_STYLE = {
  CASE_CREATED:      { bg:"rgba(59,130,246,.15)",  text:"#3b82f6"  },
  SURVEY_PROCESS:    { bg:"rgba(168,85,247,.15)",  text:"#a855f7"  },
  WORKSHOP_REPAIR:   { bg:"rgba(249,115,22,.15)",  text:"#f97316"  },
  DELIVERY_PAYMENT:  { bg:"rgba(20,184,166,.15)",  text:"#14b8a6"  },
  CLAIM_SETTLEMENT:  { bg:"rgba(34,197,94,.15)",   text:"#22c55e"  },
};

function StagePill({ stage }) {
  const c = STAGE_TABLE_STYLE[stage] || { bg:"rgba(100,116,139,.15)", text:"#94a3b8" };
  return (
    <span style={{ fontSize:12, fontWeight:600, padding:"3px 8px", borderRadius:6, background:c.bg, color:c.text, whiteSpace:"nowrap" }}>
      {STAGE_SHORT[stage] || stage}
    </span>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();

  // ── Real data: uncomment when backend ready ───────────────────────────────
  const { data, isLoading, isError } = useDashboardStats();
  const stats = data || MOCK;
  const caseInfo = data;

  const summary = stats?.summary || {};
  const apiKpi = Array.isArray(stats?.kpi) ? stats.kpi : [];
  const kpiCards = KPI_META.map((meta) => {
    const fromSummary = summary?.[meta.key];
    const fromApiKpi = apiKpi.find((item) => item?.key === meta.key || item?.label === meta.label);
    const mockKpi = MOCK.kpi.find((item) => item?.label === meta.label);
    const apiTrend = fromApiKpi?.trend;

    return {
      ...meta,
      value: Number(fromSummary ?? fromApiKpi?.value) || 0,
      trend: apiTrend == null ? (mockKpi?.trend || "") : apiTrend,
    };
  });

  // Shared table cell styles — defined here so th and td use identical padding/alignment
  const TH = {
    padding:"11px 14px", textAlign:"left", fontSize:11, fontWeight:700,
    color:"#ffffff", letterSpacing:".06em", textTransform:"uppercase",
    whiteSpace:"nowrap",
  };
  const TD = {
    padding:"13px 14px", verticalAlign:"middle",
  };

  if (isLoading) return <Spinner full />;
  if (isError)   return <ErrorState message="Failed to load dashboard" />;

  return (
    <div className="wd-animate" style={{ display:"flex", flexDirection:"column", gap:16, minWidth:0, boxSizing:"border-box", width:"100%" }}>

      {/* ── KPI Cards ───────────────────────────────────────────────────── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(6,minmax(0,1fr))", gap:12 }}>
        {kpiCards.map((k, i) => (
          <div key={k.label}
            style={{
              background: k.bg,
              borderRadius:14,
              padding:"16px 18px",
              cursor:"pointer",
              position:"relative",
              overflow:"hidden",
              minHeight:130,
              display:"flex",
              flexDirection:"column",
              justifyContent:"space-between",
            }}
            onClick={() => navigate("/admin/cases")}
          >
            {/* Icon top-right */}
            <div style={{ position:"absolute", top:14, right:14, opacity:.9 }}>
              {KPI_ICONS[i]}
            </div>
            {/* Label */}
            <div style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,.6)", textTransform:"uppercase", letterSpacing:".06em", lineHeight:1.3, paddingRight:36 }}>
              {k.label}
            </div>
            {/* Value */}
            <div style={{ fontSize:36, fontWeight:800, color:"#fff", lineHeight:1, marginTop:8 }}>
              {k.value}
            </div>
            {/* Trend */}
            {k.trend && (
              <div style={{ fontSize:11, fontWeight:700, color:k.color, marginTop:6 }}>
                {k.trend}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Main body: left (pipeline + recent) | right (SLA + timeline) ── */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 240px", gap:16, alignItems:"start", minWidth:0, overflow:"hidden" }}>

        {/* LEFT COLUMN */}
        <div style={{ display:"flex", flexDirection:"column", gap:14, minWidth:0, overflow:"hidden" }}>

          {/* Pipeline stage breadcrumb */}
          <div style={{ display:"flex", background:"var(--wd-card)", borderRadius:10, overflow:"hidden", border:"1px solid var(--wd-border)" }}>
            {Object.entries(STAGE_SHORT).map(([key, label], i, arr) => (
              <div key={key}
                style={{
                  flex:1, display:"flex", alignItems:"center", justifyContent:"space-between",
                  padding:"11px 14px",
                  background: i === 0 ? "rgba(59,130,246,.12)" : "transparent",
                  borderRight: i < arr.length-1 ? "1px solid var(--wd-border)" : "none",
                  cursor:"pointer",
                }}
                onClick={() => navigate(`/admin/cases?stage=${key}`)}
              >
                <span style={{ fontSize:12, fontWeight:600, color: i === 0 ? "#fff" : "var(--wd-t2)", whiteSpace:"nowrap" }}>
                  {label}
                </span>
                {i < arr.length-1 && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--wd-t3)" strokeWidth="2.5">
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                )}
              </div>
            ))}
          </div>

          {/* Pipeline cards — one per stage, most recent case */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,minmax(0,1fr))", gap:12 }}>
            {(stats.pipeline.length > 0 ? stats.pipeline : [
              { stage:"CASE_CREATED", no:null }, { stage:"SURVEY_PROCESS", no:null },
              { stage:"WORKSHOP_REPAIR", no:null }, { stage:"DELIVERY_PAYMENT", no:null },
            ]).map((c) => (
              <div key={c.stage} className="wd-card"
                style={{
                  padding:16, borderRadius:12, minHeight:120,
                  cursor: c.no ? "pointer" : "default",
                  transition:"border-color .15s",
                  display:"flex", flexDirection:"column",
                  justifyContent: c.no ? "flex-start" : "center",
                }}
                onClick={() => c.no && navigate(`/admin/cases/${c.no}`)}
                onMouseEnter={e => c.no && (e.currentTarget.style.borderColor = "var(--wd-blue)")}
                onMouseLeave={e => c.no && (e.currentTarget.style.borderColor = "var(--wd-border)")}
              >
                {c.no ? (
                  <>
                    <div style={{ fontFamily:"var(--wd-mono)", fontSize:13, fontWeight:800, color:"var(--wd-blue)", marginBottom:4 }}>
                      {c.no}
                    </div>
                    <div style={{ fontWeight:700, fontSize:13, color:"var(--wd-t1)", marginBottom:1 }}>{c.name}</div>
                    <div style={{ fontSize:11, color:"var(--wd-t3)", fontFamily:"var(--wd-mono)", marginBottom:12 }}>{c.vehicle}</div>
                    <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", minWidth:0, overflow:"hidden" }}>
                      <StatusPill status={c.status} />
                      {c.sla === "BREACHED" && (
                        <span style={{ fontSize:10, fontWeight:800, color:"#ef4444", padding:"2px 7px", borderRadius:10, background:"rgba(239,68,68,.15)", border:"1px solid rgba(239,68,68,.3)" }}>
                          High
                        </span>
                      )}
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign:"center" }}>
                    <div style={{ fontSize:12, color:"var(--wd-t3)", fontWeight:600, opacity:.6 }}>
                      {STAGE_SHORT[c.stage]}
                    </div>
                    <div style={{ fontSize:11, color:"var(--wd-t3)", marginTop:4, opacity:.4 }}>No cases</div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Recent Cases */}
          <div className="wd-card" style={{ padding:0, overflow:"hidden" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"16px 20px 14px" }}>
              <span style={{ fontSize:15, fontWeight:700, color:"var(--wd-t1)" }}>Recent Cases</span>
              <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => navigate("/admin/cases")}>
                View All →
              </button>
            </div>
            <div className="wd-table-wrap">
              <table style={{ width:"100%", borderCollapse:"collapse", tableLayout:"auto" }}>
                <thead>
                  <tr style={{ background:"rgba(59,130,246,.15)", borderBottom:"2px solid rgba(59,130,246,.3)"}}>
                    <th style={TH}>CASE NO</th>
                    <th style={TH}>VEHICLE NO</th>
                    <th style={TH}>CUSTOMER</th>
                    <th style={{ ...TH, width:135 }}>STAGE</th>
                    <th style={{ ...TH, width:160 }}>STATUS</th>
                    <th style={TH}>SURVEYOR</th>
                    <th style={TH}>LAST UPDATE</th>
                    <th style={TH}>SLA</th>
                    <th style={TH}></th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent.length > 0 ? stats.recent.map((c, i) => (
                    <tr key={i}
                      onClick={() => navigate(`/admin/cases/${c.no}`)}
                      style={{ borderBottom:"1px solid var(--wd-border)", cursor:"pointer", transition:"background .12s", background:"transparent" }}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--wd-hover)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <td style={TD}><span style={{ fontFamily:"var(--wd-mono)", fontSize:12, fontWeight:700, color:"var(--wd-blue)" }}>{c.no}</span></td>
                      <td style={TD}><span style={{ fontFamily:"var(--wd-mono)", fontSize:11, color:"var(--wd-t3)" }}>{c.vehicle}</span></td>
                      <td style={TD}><span style={{ fontWeight:600, fontSize:13, color:"var(--wd-t1)", whiteSpace:"nowrap" }}>{c.name}</span></td>
                      <td style={{ ...TD, maxWidth:135, overflow:"hidden" }}><StagePill stage={c.stage} /></td>
                      <td style={{ ...TD, maxWidth:160, overflow:"hidden" }}><StatusPill status={c.status} /></td>
                      <td style={TD}>
                        {c.surveyor
                          ? <span style={{ fontSize:12, color:"var(--wd-t2)", whiteSpace:"nowrap" }}>{c.surveyor}</span>
                          : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--wd-t3)" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                        }
                      </td>
                      <td style={TD}><span style={{ fontSize:12, color:"var(--wd-t3)", whiteSpace:"nowrap" }}>{c.ago}</span></td>
                      <td style={TD}>
                        {c.sla === "BREACHED"
                          ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                          : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--wd-t3)" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        }
                      </td>
                      <td style={TD}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--wd-t3)" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={9} style={{ padding:"32px 20px", textAlign:"center" }}>
                        <div style={{ fontSize:13, color:"var(--wd-t3)", fontWeight:600 }}>No recent cases</div>
                        <div style={{ fontSize:11, color:"var(--wd-t3)", marginTop:4, opacity:.7 }}>Cases will appear here once created</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN — SLA Alerts + Activity Timeline */}
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

          {/* SLA Alerts */}
          <div className="wd-card" style={{ padding:18 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
              <span style={{ fontSize:14, fontWeight:700, color:"var(--wd-t1)" }}>SLA Alerts</span>
              <button className="wd-btn wd-btn-ghost wd-btn-sm" style={{ fontSize:11, padding:"2px 8px" }}
                onClick={() => navigate("/admin/cases")}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {stats.slaAlerts.length > 0 ? stats.slaAlerts.map((a, i) => (
                <div key={i} style={{
                  display:"flex", gap:10, alignItems:"flex-start",
                  padding:"10px 12px", borderRadius:8,
                  background:`${a.color === "#ef4444" ? "rgba(239,68,68,.07)" : "rgba(249,115,22,.07)"}`,
                  border:`1px solid ${a.color}33`,
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={a.color} strokeWidth="2" style={{ flexShrink:0, marginTop:1 }}>
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:"var(--wd-t1)", lineHeight:1.3 }}>{a.type}</div>
                    <div style={{ fontSize:11, color:"var(--wd-t3)", marginTop:2 }}>
                      <span style={{ fontFamily:"var(--wd-mono)", fontWeight:600, color:a.color }}>{a.caseNo}</span>
                      {" "}{a.detail}
                    </div>
                  </div>
                </div>
              )) : (
                <div style={{ fontSize:12, color:"var(--wd-t3)", textAlign:"center", padding:"16px 0" }}>No SLA alerts</div>
              )}
            </div>
          </div>

          {/* Activity Timeline */}
          <div className="wd-card" style={{ padding:18 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <span style={{ fontSize:14, fontWeight:700, color:"var(--wd-t1)" }}>Activity Timeline</span>
              <button className="wd-btn wd-btn-ghost wd-btn-sm" style={{ fontSize:11, padding:"2px 8px" }}
                onClick={() => navigate("/admin/cases")}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            </div>
            <div style={{ display:"flex", flexDirection:"column" }}>
              {stats.timeline.length > 0 ? stats.timeline.map((group, gi) => (
                <div key={gi} style={{ display:"flex", gap:10 }}>
                  {/* Dot + line */}
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", flexShrink:0, paddingTop:2 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:group.dot, flexShrink:0 }} />
                    {gi < stats.timeline.length - 1 && (
                      <div style={{ width:1, flex:1, background:"var(--wd-border)", minHeight:28, marginTop:3 }} />
                    )}
                  </div>
                  {/* Content */}
                  <div style={{ paddingBottom: gi < stats.timeline.length-1 ? 18 : 0, flex:1 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"var(--wd-t2)", marginBottom:4 }}>{group.time}</div>
                    {group.items.map((e, ei) => (
                      <div key={ei} style={{ fontSize:12, color:"var(--wd-t3)", lineHeight:1.5 }}>
                        <span style={{ color:"var(--wd-t3)", marginRight:4 }}>·</span>
                        <span style={{ fontWeight:600, color:"var(--wd-t2)" }}>{e.label}</span>
                        <span style={{ color:"var(--wd-t3)" }}>{e.mid}</span>
                        <span style={{ fontWeight:700, color:e.actorColor }}>{e.actor}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )) : (
                <div style={{ fontSize:12, color:"var(--wd-t3)", textAlign:"center", padding:"16px 0" }}>No recent activity</div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}